import type {
  AnalyticsCommentDto,
  AnalyticsOverviewDto,
  CsatTrendPointDto,
  HeatmapPointDto,
  LowScoreOverviewDto,
  LowScoreReasonPointDto,
  NpsTrendPointDto,
  ServiceRecoveryFunnelPointDto
} from "@hotel-surveys/shared";

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 1800);
  const isFormData = init?.body instanceof FormData;
  const method = (init?.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = isFormData ? {} : { "content-type": "application/json" };
  const csrfToken = readCookie("csrfToken");
  if (csrfToken && !["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers["x-csrf-token"] = csrfToken;
  }
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    signal: init?.signal ?? controller.signal,
    headers: {
      ...headers,
      ...(init?.headers ?? {})
    }
  }).finally(() => window.clearTimeout(timeout));
  if (!response.ok) {
    throw new ApiError(response.status, await response.text());
  }
  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
  }
}

export function formatApiError(error: unknown): string {
  if (error instanceof ApiError) {
    try {
      const parsed = JSON.parse(error.message) as { error?: unknown; message?: unknown };
      const message = parsed.error ?? parsed.message;
      if (typeof message === "string" && message.trim()) return message;
    } catch {
      // Fall through to the raw message below.
    }
    return error.message || `HTTP ${error.status}`;
  }
  return error instanceof Error ? error.message : "Unknown error";
}

function readCookie(name: string) {
  return document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

export type Overview = AnalyticsOverviewDto;
export type NpsTrendPoint = NpsTrendPointDto;
export type CsatTrendPoint = CsatTrendPointDto;
export type HeatmapPoint = HeatmapPointDto;

export type QrRoute = {
  id: string;
  shortPath: string;
  label: string;
  status: string;
  unitCode?: string | null;
  hotel: { name: string };
  location: { name: string };
  subLocation?: { id: string; name: string; code: string; status: string } | null;
};

export type Survey = {
  id: string;
  name: string;
  status: string;
  defaultLanguage: string;
  settings?: {
    roomPromptTiming?: "before_submit" | "after_thank_you";
    roomLabel?: string;
    roomRequired?: boolean;
    allowRoomCorrection?: boolean;
    languageOrder?: string[];
    contact?: import("@hotel-surveys/shared").ContactSettings;
  };
  questions: Array<{
    id: string;
    internalKey: string;
    type: string;
    displayOrder: number;
    active?: boolean;
    required?: boolean;
    criticalFlag?: boolean;
    alertThreshold?: number | null;
    enableLowScoreFollowup?: boolean;
    lowScoreThreshold?: number;
    lowScoreReasonGroupId?: string | null;
    lowScoreFollowupRequired?: boolean;
    analyticsCategory?: string | null;
    config?: Record<string, unknown> | null;
    translations: Array<{
      language: string;
      label: string;
      helperText?: string | null;
      placeholder?: string | null;
      options?: unknown;
      status?: string;
    }>;
  }>;
  translations: Array<{ language: string; title: string; status: string }>;
};

export type Hotel = {
  id: string;
  name: string;
  code: string;
  brandName?: string | null;
  googleReviewUrl?: string | null;
  placeId?: string | null;
  reviewPromptTranslations?: Record<string, { title?: string; message?: string; action?: string; dismiss?: string }>;
  timezone: string;
  status: string;
  logoAsset?: { id: string; storageKey: string; originalName: string } | null;
};

export type Organization = {
  id: string;
  name: string;
  slug: string;
  defaultTimezone: string;
};

export type User = {
  id: string;
  email: string;
  name: string;
  status: string;
  passwordResetRequired?: boolean;
  roles: Array<{ id: string; role: string; hotelId?: string | null; locationId?: string | null; locationTypeId?: string | null; permissions?: string[] | null }>;
};

export type UserInvitation = {
  id: string;
  email: string;
  name: string;
  role: string;
  hotelId?: string | null;
  locationId?: string | null;
  status: string;
  expiresAt: string;
  createdAt: string;
  inviteUrl?: string;
  roles?: Array<{ role: string; hotelId?: string | null; locationId?: string | null; locationTypeId?: string | null; permissions?: string[] | null }>;
  hotel?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
};

export type FeatureFlag = {
  id: string;
  key: string;
  enabled: boolean;
  scope: string;
  scopeId: string;
  hotelId?: string | null;
  config?: unknown;
  hotel?: { id: string; name: string } | null;
};

export type AuditLog = {
  id: string;
  actorUserId?: string | null;
  action: string;
  entityType: string;
  entityId?: string | null;
  organizationId: string;
  hotelId?: string | null;
  ipHash?: string | null;
  userAgentHash?: string | null;
  metadata?: unknown;
  createdAt: string;
};

export type LocationType = {
  id: string;
  code: string;
  name: string;
};

export type Location = {
  id: string;
  code: string;
  name: string;
  floor?: string | null;
  zone?: string | null;
  googleReviewUrl?: string | null;
  placeId?: string | null;
  status: string;
  hotelId: string;
  hotel: { id: string; name: string; code: string };
  locationType: { id: string; code: string; name: string };
};

export type PermissionMatrix = {
  permissions: string[];
  rolePermissions: Record<string, string[]>;
};

export type EffectiveAccess = {
  organizationIds: string[];
  hotelIds: string[];
  locationIds: string[];
  locationTypeIds: string[];
  permissions: string[];
  roles: Array<{
    role: string;
    organizationId?: string | null;
    hotelId?: string | null;
    locationId?: string | null;
    locationTypeId?: string | null;
    permissions: string[];
  }>;
  canViewPII: {
    room: boolean;
    contact: boolean;
    comment: boolean;
    export: boolean;
  };
  isSuperAdmin: boolean;
  isOrgWide: boolean;
};

export type SubLocation = {
  id: string;
  hotelId: string;
  locationId: string;
  name: string;
  code: string;
  type: string;
  googleReviewUrl?: string | null;
  placeId?: string | null;
  status: string;
  hotel: { id: string; name: string; code: string };
  location: { id: string; name: string; code: string };
};

export type SurveyRule = {
  id: string;
  name: string;
  priority: number;
  mealPeriod?: string | null;
  status: string;
  timeWindows?: Array<{ start: string; end: string }> | null;
  daysOfWeek?: number[] | null;
  survey: { id: string; name: string };
  hotel?: { id: string; name: string } | null;
  locationType?: { id: string; name: string } | null;
  location?: { id: string; name: string } | null;
};

export type SurveyPreviewResolution = {
  qrRouteId: string;
  shortPath: string;
  mealPeriod: string;
  matchedRuleId: string | null;
  surveyVersionId: string | null;
  surveyId: string | null;
  surveyName: string | null;
  versionNumber: number | null;
};

export type Alert = {
  id: string;
  severity: string;
  reason: string;
  status: string;
  createdAt: string;
  sentAt?: string | null;
  hotel: { id: string; name: string };
  location: { id: string; name: string };
};

export type AlertDetail = Alert & {
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
  notificationPayload?: unknown;
  submission: {
    id: string;
    submittedAt: string;
    requestPath?: string | null;
    deviceType?: string | null;
    language: string;
    mealPeriod?: string | null;
    unitCode?: string | null;
    subLocation?: { id: string; name: string; code: string } | null;
    contactPermission: boolean;
    roomNumber?: string | null;
    roomRedacted?: boolean;
    contactEmail?: string | null;
    contactPhone?: string | null;
    contactName?: string | null;
    contactRedacted?: boolean;
  };
  lowScoreFollowUp?: {
    triggeredCategories: string[];
    selectedReasons: string[];
    skippedComment: boolean;
    contactOffered: boolean;
    contactPermission?: boolean | null;
    comment?: string | null;
    commentRedacted: boolean;
  } | null;
  answers: Array<{
    questionKey: string;
    answerType: string;
    numericValue?: number | null;
    booleanValue?: boolean | null;
    optionValue?: string | null;
    textValue?: string | null;
    textRedacted: boolean;
    rawValue?: unknown;
  }>;
};

export type IncidentEvent = {
  id: string;
  incidentId: string;
  actorUserId?: string | null;
  type: string;
  note?: string | null;
  metadata?: unknown;
  createdAt: string;
};

export type Incident = {
  id: string;
  incidentNumber: string;
  severity: string;
  status: string;
  title: string;
  summary?: string | null;
  assignedToUserId?: string | null;
  dueAcknowledgeAt?: string | null;
  dueResolveAt?: string | null;
  acknowledgedAt?: string | null;
  resolvedAt?: string | null;
  closedAt?: string | null;
  slaBreached: boolean;
  createdAt: string;
  hotel: { id: string; name: string };
  location: { id: string; name: string };
  alert?: Alert;
  submission: {
    unitCode?: string | null;
    submittedAt: string;
    language: string;
    mealPeriod?: string | null;
    requestPath?: string | null;
  };
  events?: IncidentEvent[];
};

export type IncidentDetail = Omit<Incident, "submission"> & {
  alert: Alert;
  events: IncidentEvent[];
  submission: AlertDetail["submission"] & {
    lowScoreFollowUp?: AlertDetail["lowScoreFollowUp"];
    answers: AlertDetail["answers"];
  };
};

export type AlertRecipient = {
  id: string;
  hotelId: string;
  locationId?: string | null;
  roleLabel: string;
  email: string;
  active: boolean;
  hotel: { id: string; name: string };
  location?: { id: string; name: string } | null;
};

export type AnalyticsComment = AnalyticsCommentDto;
export type LowScoreOverview = LowScoreOverviewDto;
export type LowScoreReasonPoint = LowScoreReasonPointDto;

export type LowScoreReasonTranslation = {
  id: string;
  language: string;
  label: string;
};

export type LowScoreReason = {
  id: string;
  reasonGroupId: string;
  value: string;
  displayOrder: number;
  active: boolean;
  translations: LowScoreReasonTranslation[];
};

export type LowScoreReasonGroup = {
  id: string;
  key: string;
  name: string;
  category: string;
  active: boolean;
  reasons: LowScoreReason[];
};

export type ServiceRecoveryFunnelPoint = ServiceRecoveryFunnelPointDto;

export type PrivacyRequest = {
  id: string;
  type: "export" | "delete" | "rectify";
  requesterReference: string;
  status: string;
  notes?: string | null;
  createdAt: string;
  completedAt?: string | null;
};

export type ReportSchedule = {
  id: string;
  hotelId?: string | null;
  frequency: "weekly" | "monthly";
  recipients: string[];
  filters?: Record<string, unknown>;
  active: boolean;
  nextRunAt: string;
  hotel?: { id: string; name: string } | null;
};
